import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { useClientStore } from '../../store/useStore';
import { Delete, Shield, Eye, EyeOff, Check, Lock, KeyRound } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ClientChangePin() {
  const { nickname } = useParams();
  const navigate = useNavigate();
  const { client, setPinChanged, isClientAuthenticated, getClientToken } = useClientStore();

  const isFirstTime = !client?.pin_changed;

  // Steps:
  //  First-time: step 1 = new PIN, step 2 = confirm PIN (current = '9999' auto)
  //  Settings:   step 1 = current PIN, step 2 = new PIN, step 3 = confirm PIN
  const totalSteps = isFirstTime ? 2 : 3;
  const [step, setStep] = useState(1);
  const [currentPin, setCurrentPin] = useState(isFirstTime ? '9999' : '');
  const [newPin, setNewPin]         = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [loading, setLoading]       = useState(false);
  const [showPin, setShowPin]       = useState(false);
  const [error, setError]           = useState('');
  const advanceTimerRef             = useRef(null);

  const getToken = useCallback(() => getClientToken() || localStorage.getItem('client_token'), [getClientToken]);

  useEffect(() => {
    if (!isClientAuthenticated && !getToken()) navigate(`/clientes/${nickname}`);
  }, [isClientAuthenticated, navigate, nickname, getToken]);

  const getCurrentValue = useCallback(() => {
    if (isFirstTime) {
      return step === 1 ? newPin : confirmPin;
    }
    if (step === 1) return currentPin;
    if (step === 2) return newPin;
    return confirmPin;
  }, [isFirstTime, step, currentPin, newPin, confirmPin]);

  const setCurrentValue = useCallback((value) => {
    if (isFirstTime) {
      if (step === 1) setNewPin(value);
      else setConfirmPin(value);
    } else {
      if (step === 1) setCurrentPin(value);
      else if (step === 2) setNewPin(value);
      else setConfirmPin(value);
    }
  }, [isFirstTime, step]);

  const handleKeyPress = useCallback((num) => {
    const val = getCurrentValue();
    if (val.length < 4) {
      setCurrentValue(val + num);
      setError('');
    }
  }, [getCurrentValue, setCurrentValue]);

  const handleDelete = useCallback(() => {
    const val = getCurrentValue();
    setCurrentValue(val.slice(0, -1));
    setError('');
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  }, [getCurrentValue, setCurrentValue]);

  const handleClear = useCallback(() => {
    setCurrentValue('');
    setError('');
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  }, [setCurrentValue]);

  const submitPinChange = useCallback(async (cpn, npn) => {
    setLoading(true);
    setError('');
    const token = getToken();
    if (!token) {
      setError('Sesión expirada. Vuelve a iniciar sesión.');
      setLoading(false);
      setTimeout(() => navigate(`/clientes/${nickname}`), 1500);
      return;
    }
    try {
      await axios.post(`${API_URL}/api/clients/auth/change-pin`,
        { current_pin: cpn, new_pin: npn },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPinChanged();
      toast.success('¡PIN configurado correctamente!');
      navigate(`/clientes/${nickname}/dashboard`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Error al cambiar PIN';
      setError(msg);
      setNewPin('');
      setConfirmPin('');
      setStep(isFirstTime ? 1 : 2);
    } finally {
      setLoading(false);
    }
  }, [getToken, navigate, nickname, setPinChanged, isFirstTime]);

  const advance = useCallback(() => {
    const val = getCurrentValue();
    if (val.length < 4) return;

    if (isFirstTime) {
      if (step === 1) {
        if (val === '9999') { setError('Elige un PIN diferente al código genérico'); setNewPin(''); return; }
        setStep(2);
      } else {
        if (val !== newPin) { setError('Los PINs no coinciden'); setConfirmPin(''); return; }
        submitPinChange('9999', newPin);
      }
    } else {
      if (step === 1) {
        setStep(2);
      } else if (step === 2) {
        if (val === currentPin) { setError('El nuevo PIN debe ser diferente al actual'); setNewPin(''); return; }
        if (val === '9999')     { setError('No puedes usar el PIN genérico'); setNewPin(''); return; }
        setStep(3);
      } else {
        if (val !== newPin) { setError('Los PINs no coinciden'); setConfirmPin(''); return; }
        submitPinChange(currentPin, newPin);
      }
    }
  }, [getCurrentValue, isFirstTime, step, newPin, currentPin, submitPinChange]);

  // Auto-advance 400 ms after 4th digit is entered
  useEffect(() => {
    const val = getCurrentValue();
    if (val.length === 4 && !loading) {
      advanceTimerRef.current = setTimeout(() => { advance(); }, 400);
    }
    return () => { if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current); };
  }, [getCurrentValue, loading, advance]);

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key >= '0' && e.key <= '9') handleKeyPress(e.key);
      else if (e.key === 'Backspace') handleDelete();
      else if (e.key === 'Escape') handleClear();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyPress, handleDelete, handleClear]);

  const currentValue = getCurrentValue();

  const getStepTitle = () => {
    if (isFirstTime) return step === 1 ? 'Crea tu PIN personal' : 'Confirma tu nuevo PIN';
    if (step === 1) return 'PIN actual';
    if (step === 2) return 'Nuevo PIN';
    return 'Confirmar PIN';
  };

  const getStepDesc = () => {
    if (isFirstTime) return step === 1
      ? 'Elige 4 dígitos que recuerdes bien'
      : 'Ingresa el mismo PIN para confirmar';
    if (step === 1) return 'Ingresa tu PIN actual para continuar';
    if (step === 2) return 'Elige un nuevo PIN seguro de 4 dígitos';
    return 'Repite el nuevo PIN para confirmar';
  };

  const dots = isFirstTime
    ? [{ s: 1, label: 'Nuevo' }, { s: 2, label: 'Confirmar' }]
    : [{ s: 1, label: 'Actual' }, { s: 2, label: 'Nuevo' }, { s: 3, label: 'Confirmar' }];

  const numpadRows = [['1','2','3'],['4','5','6'],['7','8','9'],['clear','0','delete']];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="relative w-20 h-20 mx-auto mb-4">
            <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/30">
              {isFirstTime ? <KeyRound className="w-10 h-10 text-white" /> : <Shield className="w-10 h-10 text-white" />}
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">
            {isFirstTime ? 'Configura tu PIN' : 'Cambiar PIN'}
          </h1>
          <p className="text-slate-400 text-sm">
            {isFirstTime ? 'Solo necesitas hacer esto una vez' : 'Actualiza tu PIN de acceso'}
          </p>
        </div>

        {/* Step progress */}
        <div className="flex items-center justify-center gap-3 mb-6">
          {dots.map((d, idx) => (
            <div key={d.s} className="flex items-center gap-3">
              <div className="flex flex-col items-center gap-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                  d.s < step ? 'bg-emerald-500 text-white' :
                  d.s === step ? 'bg-orange-500 text-white scale-110 shadow-lg shadow-orange-500/30' :
                  'bg-slate-700 text-slate-500'
                }`}>
                  {d.s < step ? <Check className="w-4 h-4" /> : d.s}
                </div>
                <span className={`text-xs ${d.s === step ? 'text-orange-400' : 'text-slate-600'}`}>{d.label}</span>
              </div>
              {idx < dots.length - 1 && (
                <div className={`w-8 h-0.5 mb-4 transition-all duration-300 ${d.s < step ? 'bg-emerald-500' : 'bg-slate-700'}`} />
              )}
            </div>
          ))}
        </div>

        {/* PIN Card */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-6 border border-slate-700/50 shadow-2xl">
          <div className="mb-6 text-center">
            <p className="text-white font-semibold text-lg mb-0.5">{getStepTitle()}</p>
            <p className="text-slate-400 text-sm">{getStepDesc()}</p>
          </div>

          {/* PIN dots display */}
          <div className="flex items-center justify-center gap-2 mb-2 relative">
            <div className="flex gap-4">
              {[...Array(4)].map((_, i) => {
                const filled = i < currentValue.length;
                const isConfirm = isFirstTime ? step === 2 : step === totalSteps;
                return (
                  <div key={i} className={`w-14 h-16 rounded-2xl border-2 flex items-center justify-center text-2xl font-bold transition-all duration-200 ${
                    filled
                      ? isConfirm
                        ? 'border-emerald-500 bg-emerald-500/20 text-emerald-300 scale-105'
                        : 'border-orange-500 bg-orange-500/20 text-orange-300 scale-105'
                      : 'border-slate-600 bg-slate-700/50 text-slate-500'
                  }`}>
                    {filled ? (showPin ? currentValue[i] : '●') : ''}
                  </div>
                );
              })}
            </div>
            <button
              onClick={() => setShowPin(v => !v)}
              className="absolute -right-1 p-2 text-slate-500 hover:text-slate-300 transition-colors"
            >
              {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {/* Progress indicator (filling in) */}
          <div className="flex gap-1 justify-center mb-4 h-1">
            {[...Array(4)].map((_, i) => (
              <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-200 ${
                i < currentValue.length ? 'bg-orange-500' : 'bg-slate-700'
              }`} />
            ))}
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
              <p className="text-red-400 text-sm text-center">{error}</p>
            </div>
          )}

          {/* Numpad */}
          <div className="grid gap-2.5">
            {numpadRows.map((row, ri) => (
              <div key={ri} className="grid grid-cols-3 gap-2.5">
                {row.map((btn) => {
                  if (btn === 'clear') return (
                    <button key={btn} onClick={handleClear}
                      className="h-14 rounded-2xl bg-slate-700/60 text-slate-400 text-sm font-medium hover:bg-slate-600/60 active:scale-95 transition-all duration-150">
                      Limpiar
                    </button>
                  );
                  if (btn === 'delete') return (
                    <button key={btn} onClick={handleDelete}
                      className="h-14 rounded-2xl bg-slate-700/60 text-slate-400 hover:bg-slate-600/60 active:scale-95 transition-all duration-150 flex items-center justify-center">
                      <Delete className="w-5 h-5" />
                    </button>
                  );
                  return (
                    <button key={btn} onClick={() => handleKeyPress(btn)}
                      className="h-14 rounded-2xl bg-slate-700 text-white text-2xl font-semibold hover:bg-slate-600 active:scale-95 active:bg-orange-500/20 transition-all duration-150">
                      {btn}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Loading state */}
          {loading && (
            <div className="flex items-center justify-center gap-2 mt-4 text-orange-400">
              <div className="w-5 h-5 border-2 border-orange-400/30 border-t-orange-400 rounded-full animate-spin" />
              <span className="text-sm">Guardando PIN...</span>
            </div>
          )}

          {/* Hint */}
          {!loading && currentValue.length < 4 && (
            <p className="text-center text-slate-600 text-xs mt-4">
              Ingresa 4 dígitos para continuar automáticamente
            </p>
          )}
          {!loading && currentValue.length === 4 && (
            <p className="text-center text-orange-400 text-xs mt-4 animate-pulse">
              Avanzando...
            </p>
          )}
        </div>

        <p className="text-center text-slate-600 text-xs mt-6">
          Powered by <span className="text-orange-400 font-medium">OkReserva</span>
        </p>
      </div>
    </div>
  );
}
